import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ms from 'ms';
import Image from 'next/image';
import Router from 'next/router';

import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { useEmailNotifications } from '@components/EmailNotifications/hooks';
import { Button } from '@components/main-components/button/Button';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { Modal } from '@components/main-components/modals/Modal';
import { useAuth } from '@context/auth/base';
import { ModalContext } from '@context/modal';
import { AuthUserDocument, useQuery } from '@graphql/reactQuery';
import { useAuthArtistApplicationStatus } from '@hooks/useAuthArtistApplicationStatus';
import { NAV_LINKS } from '@utils/constants';
import { webappUriPath } from '@utils/url';

export const ApplicationSubmittedModal = ({ nav }: { nav?: boolean }) => {
  const { closeCancelModal } = ModalContext.useContainer();
  const { authUser, authFilterQueryKey } = useAuth();
  const { email } = useEmailNotifications();

  const { status } = useAuthArtistApplicationStatus();

  const isPending = status === 'PENDING';

  const isAccepted = status === 'ACCEPTED';

  useQuery(AuthUserDocument, {
    enabled: isPending,
    staleTime: 0,
    refetchInterval: ms('5 seconds'),
    filterQueryKey: authFilterQueryKey,
  });

  const userWebappUri = authUser?.webappUri;
  const isArtist = !!authUser?.artist;

  useEffect(() => {
    if (!userWebappUri) return;

    if (isAccepted || isArtist) Router.push(webappUriPath(userWebappUri));
  }, [isAccepted, isArtist, userWebappUri]);

  const body = (
    <div className="relative -mt-3 flex w-full flex-col items-center justify-center gap-y-8">
      {nav && (
        <div className="absolute right-0 top-0 flex">
          <IconButton
            icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
            onClick={closeCancelModal}
          />
        </div>
      )}
      <div className="flex flex-col items-center justify-center gap-y-4 text-center">
        <div className="relative flex h-36 w-36">
          <Image
            src="/images/transaction-success.svg"
            alt="transaction-success"
            fill
            sizes="144px"
          />
        </div>

        <div className="flex flex-col gap-y-2 font-base text-base800">
          <h2 className="text-base-l font-semibold md:text-base-xl">
            Application has been submitted!
          </h2>
          <p className="text-base-m md:text-base-l">
            We&apos;re reviewing your application to verify <br />
            your profile. We&apos;ll reach back out <br /> to you{' '}
            {!!email ? `at ${email}` : 'via email'}
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center gap-x-2 [&_a]:w-full [&_button]:w-full ">
        {/* TODO: https://linear.app/sound/issue/ENG-5731/update-learn-more-link-to-accurate-one */}
        <Button
          href={NAV_LINKS.About}
          label="Learn More"
          variant={nav ? 'primary' : 'tertiary'}
          className="min-w-[unset] max-w-[unset]"
          fullWidth={nav}
        />
        {authUser?.webappUri && !nav && (
          <Button
            href={webappUriPath(authUser.webappUri)}
            label="View Profile"
            variant="primary"
            className="min-w-[unset]  max-w-[unset]"
          />
        )}
      </div>
    </div>
  );

  return <Modal body={body} />;
};
